// Generated by tutorials/record.cjs from tutorials/articles/h67-selective-touch-setup.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h67 --text-only` (wording) or
// `node tutorials/record.cjs h67` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'selective-touch-setup',
  catalogId: 'h67',
  title: 'Setting up Selective Touch',
  summary: 'Getting Started\'s Selective Touch step, part by part: the meals taken on tablets, the order screens and how a resident sees them, the servers, registering tablets, credits and printing, and a test order that proves it all works.',
  audience: 'Administrators setting up a facility where servers take residents\' orders on tablets',
  area: 'kitchen',
  learn: [
    'Choose the meals taken on tablets, and the rules on the tablet',
    'Check each meal\'s order screens, and preview them as a resident on a restricted diet sees them',
    'See which servers can unlock a tablet, and register your tablets',
    'Check credits and printing',
    'Prove the setup with a test order, then mark the step done',
  ],
  keywords: [
    'selective touch setup',
    'set up selective touch',
    'touch setup',
    'tablets',
    'register tablet',
    'tablet code',
    'servers',
    'pin',
    'order screens',
    'screen editor',
    'build from menu',
    'diet tab',
    'preview',
    'act as pos',
    'show consistency',
    'test order',
    'test resident',
    'allergen warning',
    'iddsi badge',
    'print now',
    'getting started',
  ],
  steps: [
    {
      id: 'open',
      part: 'Meals and rules',
      heading: 'Open Selective Touch',
      text: 'In Getting Started, select Selective Touch (step 11). The step is optional: if your servers do not take orders on tablets, select We don\'t use Selective Touch at the bottom.\n\nThe eight parts are listed on the left, each with where it stands: a check mark when it is done, a short note when it needs work. The step opens on the first part that needs work.',
      media: {
        image: {
          src: '/help/selective-touch-setup/open.webp',
          width: 1280,
          height: 978,
        },
        clip: {
          src: '/help/selective-touch-setup/open.mp4',
          width: 1280,
          height: 978,
          durationSec: 2,
        },
        alt: 'The Selective Touch step with its eight parts on the left, from 1 Meals and rules (Choose the meals) to 8 Test order (Not started), and Meals and rules open.',
      },
    },
    {
      id: 'meals',
      part: 'Meals and rules',
      heading: '1. Meals taken on tablets',
      text: 'Tick the meals servers take on tablets. The table shows when each meal starts and when its orders close: the tablet opens on the meal being served now.\n\nMeal times are changed in Facility basics; the cut-offs (Prevent Saving After Time) in Print Schedules.',
      media: {
        image: {
          src: '/help/selective-touch-setup/meals.webp',
          width: 998,
          height: 772,
        },
        clip: {
          src: '/help/selective-touch-setup/meals.mp4',
          width: 998,
          height: 772,
          durationSec: 4.9,
        },
        alt: 'Meals and rules: Lunch and Dinner ticked under Meals taken on tablets, and the table of meal times, with Lunch orders closing at 10:30 AM and Dinner at 3:30 PM.',
      },
    },
    {
      id: 'rules',
      part: 'Meals and rules',
      heading: 'Rules on the tablet',
      text: 'Show consistency lets servers change an item\'s consistency while ordering; the resident\'s texture always applies. Act As POS is for a cafe or counter, where every order goes through a ticket review as at a register. Leave it off when servers take residents\' orders.\n\nSelect Save. These are the same settings as in Facility Settings, Selective Touch.',
      media: {
        image: {
          src: '/help/selective-touch-setup/rules.webp',
          width: 1280,
          height: 738,
        },
        clip: {
          src: '/help/selective-touch-setup/rules.mp4',
          width: 1280,
          height: 738,
          durationSec: 3.8,
        },
        alt: 'On the tablet: Show consistency ticked and Act As POS off, Saved. beside Save, and the parts list showing Lunch, Dinner under Meals and rules.',
      },
    },
    {
      id: 'screens',
      part: 'Screens and preview',
      heading: '2. Screens',
      text: 'Each meal needs a screen of tiles, one per menu category. The checks show each meal\'s screens, tiles and diet tabs, and list anything on the menu with no tile, which could not be ordered.\n\nOpen the screen editor to build or change them: Build from Menu adds a tile for every category on your menu, a tab for a diet shows only to residents on it, and an Always Served screen holds what every resident can have.',
      media: {
        image: {
          src: '/help/selective-touch-setup/screens.webp',
          width: 998,
          height: 454,
        },
        clip: {
          src: '/help/selective-touch-setup/screens.mp4',
          width: 998,
          height: 454,
          durationSec: 2.6,
        },
        alt: 'Screens: Lunch and Dinner each with 2 screens, 10 tiles and 1 diet tab, both checked, and Open the screen editor.',
      },
    },
    {
      id: 'preview',
      part: 'Screens and preview',
      heading: '3. Preview',
      text: 'Preview shows a meal\'s screens the way a server sees them for a made-up resident. Pick the meal, a diet and a texture. It starts on a diet that has its own tab, so you see what a restricted diet changes: here the tablet opens on the Renal tab.\n\nOn a real tablet, a tile with nothing the resident may eat that day is left off, and items that clash with an allergy ask before they are added.',
      media: {
        image: {
          src: '/help/selective-touch-setup/preview.webp',
          width: 998,
          height: 898,
        },
        clip: {
          src: '/help/selective-touch-setup/preview.mp4',
          width: 998,
          height: 898,
          durationSec: 3,
        },
        alt: 'Preview for Lunch with the Renal diet and Mechanical Soft texture: the sample resident Sample, Pat with an IDDSI 6 chip, the Lunch and Renal tabs with Renal open, its tiles Renal Choices, Vegetable, Dessert and Beverage, and The tablet opens on the Renal tab for this diet.',
      },
    },
    {
      id: 'preview-regular',
      part: 'Screens and preview',
      heading: 'Try another diet',
      text: 'Pick another diet to see what changes. For a resident on Regular, the Renal tab is hidden, and the note under the screen says so.',
      media: {
        image: {
          src: '/help/selective-touch-setup/preview-regular.webp',
          width: 998,
          height: 898,
        },
        clip: {
          src: '/help/selective-touch-setup/preview-regular.mp4',
          width: 998,
          height: 898,
          durationSec: 3.5,
        },
        alt: 'The Preview with the Regular diet: only the Lunch tab, its six tiles, and Hidden for this diet: Renal.',
      },
    },
    {
      id: 'users',
      part: 'Servers, tablets and printing',
      heading: '4. Who orders',
      text: 'A server unlocks a tablet with their PIN. They need a role with a Selective Touch level of 1 or more, access to your facility, and a PIN. The list shows each user\'s touch level, whether a PIN is set, and their ticket printers; Not set means that person cannot unlock a tablet yet.\n\nPSI sets up users, roles and PINs. Ask Dietech support to add a server or set a PIN.',
      media: {
        image: {
          src: '/help/selective-touch-setup/users.webp',
          width: 998,
          height: 556,
        },
        clip: {
          src: '/help/selective-touch-setup/users.mp4',
          width: 998,
          height: 556,
          durationSec: 2.6,
        },
        alt: 'Who orders: 2 of 3 users with a Selective Touch level have a PIN, and the list of Dana Morgan, Luis Ferreira and Priya Nair, with Priya\'s PIN Not set.',
      },
    },
    {
      id: 'tablets',
      part: 'Servers, tablets and printing',
      heading: '5. Register a tablet',
      text: 'Select Register Tablet, name the tablet for where it lives, and select Register. You get a one-time setup code.\n\nOn the tablet, open dietechonline.com/tablet and type the code. From then on the tablet opens straight into Selective Touch and stays signed in; servers unlock it with their PIN.',
      media: {
        image: {
          src: '/help/selective-touch-setup/tablets.webp',
          width: 944,
          height: 566,
        },
        clip: {
          src: '/help/selective-touch-setup/tablets.mp4',
          width: 944,
          height: 566,
          durationSec: 6,
        },
        alt: 'The Tablet Setup Code dialog: On Dining Room 1 - North, open dietechonline.com/tablet and enter this code, the code K7P4-QX2M, and Done.',
      },
    },
    {
      id: 'tablet-ready',
      part: 'Servers, tablets and printing',
      heading: 'Check the tablet is registered',
      text: 'Select Done. Once the tablet has taken its code, the list shows it as Ready (the list refreshes every minute). Select Check again so the step sees it too: the tablet gets a check mark, with when it was last used.\n\nThe same list is in Facility Settings, Selective Touch Tablets, where you can also give a tablet a new code or revoke it.',
      media: {
        image: {
          src: '/help/selective-touch-setup/tablet-ready.webp',
          width: 998,
          height: 660,
        },
        clip: {
          src: '/help/selective-touch-setup/tablet-ready.mp4',
          width: 998,
          height: 660,
          durationSec: 4.8,
        },
        alt: 'Tablets: Dining Room 1 - North (last used 1 min ago) checked, and the Selective Touch Tablets list with Dining Room 1 - North Ready.',
      },
    },
    {
      id: 'credits',
      part: 'Servers, tablets and printing',
      heading: '6. Credits and payment',
      text: 'If your facility charges meals to residents\' credits, this part shows the credit meals and whether credits refill each month. At the end of a credit meal the server picks Bill to Account or Pay With Cash.\n\nIf credits are off, there is nothing to set up here.',
      media: {
        image: {
          src: '/help/selective-touch-setup/credits.webp',
          width: 998,
          height: 236,
        },
        alt: 'Credits and payment: Credits are off, so there is nothing to set up, with a link to Credits / Costs.',
      },
    },
    {
      id: 'printing',
      part: 'Servers, tablets and printing',
      heading: '7. Printing',
      text: 'When a server saves an order with Print Now, the ticket prints on that server\'s ticket printer through the kitchen\'s print agent. The checks show the tray ticket printer and the print agent, and name any server with no ticket printer turned on, who is not offered Print Now.\n\nPrinters are chosen in Printing and reports, or on Printers.',
      media: {
        image: {
          src: '/help/selective-touch-setup/printing.webp',
          width: 998,
          height: 448,
        },
        alt: 'Printing: 1 tray ticket printer set up, a print agent online, and No ticket printer is turned on for Luis Ferreira.',
      },
    },
    {
      id: 'test-make',
      part: 'The test order',
      heading: '8. Make the test resident',
      text: 'One real order proves the setup end to end: the allergy is caught, the texture shows, and the ticket prints. The step makes a test resident named TEST RESIDENT - DELETE ME, and deletes it again at the end. Your residents are not touched.\n\nPick a unit, a restricted diet, a texture and an allergy that something on today\'s menu contains, then select Make the test resident.',
      media: {
        image: {
          src: '/help/selective-touch-setup/test-make.webp',
          width: 998,
          height: 642,
        },
        clip: {
          src: '/help/selective-touch-setup/test-make.mp4',
          width: 998,
          height: 642,
          durationSec: 6,
        },
        alt: 'Make the test resident: Unit Maple, Diet Renal, Texture Minced and Moist and Allergy Peanuts, and Make the test resident.',
      },
    },
    {
      id: 'test-order',
      part: 'The test order',
      heading: 'Order on a tablet',
      text: 'On a registered tablet, unlock with a server\'s PIN and open the test resident. Add an item that contains the allergy: the allergen warning should appear. Look for the texture and its IDDSI badge. Finish the order (choose Pay With Cash if asked, so no credit is charged) and save it with Print Now.\n\nBack here, select Check again: the order is saved.',
      media: {
        image: {
          src: '/help/selective-touch-setup/test-order.webp',
          width: 998,
          height: 492,
        },
        clip: {
          src: '/help/selective-touch-setup/test-order.mp4',
          width: 998,
          height: 492,
          durationSec: 3.3,
        },
        alt: 'Order on a tablet: TEST RESIDENT - DELETE ME is on Renal, Minced and Moist, allergic to Peanuts, the four things to do on the tablet, and The order is saved (3 lines).',
      },
    },
    {
      id: 'test-confirm',
      part: 'The test order',
      heading: 'Say what you saw',
      text: 'Under What did you see?, tick The allergen warning appeared, answer whether the IDDSI badge showed next to the texture, and tick The ticket printed. Then select Confirm the test order.\n\nIf something did not happen, fix it before confirming: the allergy protection for a missing warning, the printer for a missing ticket.',
      media: {
        image: {
          src: '/help/selective-touch-setup/test-confirm.webp',
          width: 998,
          height: 372,
        },
        clip: {
          src: '/help/selective-touch-setup/test-confirm.mp4',
          width: 998,
          height: 372,
          durationSec: 5.1,
        },
        alt: 'What did you see?: The allergen warning appeared ticked, Yes for the IDDSI badge, The ticket printed ticked, and Confirm the test order.',
      },
    },
    {
      id: 'test-delete',
      part: 'The test order',
      heading: 'Delete the test resident',
      text: 'Select Confirm the test order, then Delete the test resident, and Delete to confirm. The test resident and every order line saved for it are deleted, and the deletion is logged.\n\nThe part then shows that the test passed, and when.',
      media: {
        image: {
          src: '/help/selective-touch-setup/test-delete.webp',
          width: 998,
          height: 268,
        },
        clip: {
          src: '/help/selective-touch-setup/test-delete.mp4',
          width: 998,
          height: 268,
          durationSec: 5.8,
        },
        alt: 'Test order: The test order passed, the allergen warning appeared, the IDDSI badge showed and the ticket printed, and the test resident and its 3 order lines were deleted by Dana Morgan.',
      },
    },
    {
      id: 'done',
      part: 'The test order',
      heading: 'Mark done',
      text: 'When every check passes, the list under the parts is gone and Mark done is on. Select it: Selective Touch is marked Done and Getting Started moves on to the Go-live check.\n\nWhat the step changed stays as ordinary settings: Show consistency and Act As POS in Facility Settings, Selective Touch; the tablets in Selective Touch Tablets; the screens in the screen editor. For taking orders day to day, see Selective Touch.',
      media: {
        image: {
          src: '/help/selective-touch-setup/done.webp',
          width: 1280,
          height: 772,
        },
        clip: {
          src: '/help/selective-touch-setup/done.mp4',
          width: 1280,
          height: 772,
          durationSec: 2,
        },
        alt: 'The list of steps with Selective Touch Done, and the Go-live check open.',
      },
    },
  ],
  tips: [
    'Name each tablet for where it lives, for example Dining Room 1 - North, so you can tell them apart in the list.',
    'For the test order, pick an allergy that something on today\'s menu contains, so the allergen warning has something to catch.',
    'You can come back to this step any time: Facility Settings, Selective Touch has Set up Selective Touch step by step while Getting Started is on.',
  ],
  notes: [
    'The step is optional. If your facility does not use Selective Touch, select We don\'t use Selective Touch; nothing else depends on it.',
    'Mark done stays off until the checks pass: at least one meal chosen, a registered tablet, and a test order confirmed and cleaned up.',
    'Servers need a role with a Selective Touch level, access to your facility and a PIN. PSI sets up users, roles and PINs: ask Dietech support.',
    'The test resident is made by this step and deleted by it, with its order lines. Your residents are never touched.',
  ],
  related: [
    'getting-started',
    'selective-touch',
    'printing-setup',
    'printers-and-printing',
    'allergies-likes-dislikes',
  ],
  recorded: '2026-11-03',
};

export default article;
